import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import '../../styles/components/admin/TenantDetailView.css';

const TenantDetailView = () => {  
  const { tenantId } = useParams(); 
  const navigate = useNavigate();

  const handleBackToTenants = () => {
    navigate('/admin/tenants');
  };

  // State management
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clinic-setup');
  const [counts, setCounts] = useState({
    totalPatients: 0,
    totalProfessionals: 0,
    journalEntries: 0
  });
  
  // Tab data states
  const [patients, setPatients] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [clinicSetup, setClinicSetup] = useState(null);
  
  // Modal states
  const [showClinicModal, setShowClinicModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data states
  const [basicFormData, setBasicFormData] = useState({
    primaryEmail: '',
    name: '',
    location: ''
  });
  
  const [publicProfileData, setPublicProfileData] = useState({
    address: '',
    description: '',
    yearEstablished: '',
    contactPhone: '',
    services: [''],
    appointmentTypes: ['']
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  // Load tenant details on mount
  useEffect(() => {
    if (tenantId) {
      loadTenantDetails();
      loadTenantCounts();
      loadClinicSetup();
    }
  }, [tenantId]);
  
  // Load tab-specific data when tab changes
  useEffect(() => {
    if (activeTab === 'patients') {
      loadPatients();
    } else if (activeTab === 'doctors') {
      loadProfessionals();
    }
  }, [activeTab]);
  
  const loadTenantDetails = async () => {
    try {
      setLoading(true);
      const response = await adminService.getTenantById(tenantId);
      
      if (response.success) {
        setTenant(response.data);
      } else {
        throw new Error(response.message || 'Failed to load tenant');
      }
    } catch (err) {
      console.error('Error loading tenant:', err);
      toast.error(err.message);
      
      // Mock data for development
      if (process.env.NODE_ENV === 'development') {
        setTenant({
          _id: tenantId,
          tenantId: 'NLX-2025-001',
          name: 'MindBridge Health',
          location: 'New York, NY',
          active: true,
          adminEmail: 'mindbridgehealth@gmail.com',
          createdAt: '2025-04-19T00:00:00Z'
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const loadTenantCounts = async () => {
    try {
      const response = await adminService.getTenantCounts(tenantId);
      
      console.log('🔍 Raw counts response:', response);
      
      if (response.success) {
        setCounts(response.data);
      }
    } catch (err) {
      console.error('Error loading counts:', err);
      
      // Mock data for development
      if (process.env.NODE_ENV === 'development') {
        setCounts({
          totalPatients: 21,
          totalProfessionals: 8,
          journalEntries: 92
        });
      }
    }
  };
  
  const loadClinicSetup = async () => {
    try {
      const response = await adminService.getTenantClinicSetup(tenantId);
      
      if (response.success) {
        setClinicSetup(response.data);
      }
    } catch (err) {
      console.error('Error loading clinic setup:', err);
      
      // Mock data for development
      if (process.env.NODE_ENV === 'development') {
        setClinicSetup({
          primaryEmail: 'mindbridgehealth@gmail.com',
          clinicName: 'MindBridge Health',
          clinicLocation: 'New York, NY',
          address: '123 Wellness Blvd, Springfield',
          description: 'Leading mental health clinic specializing in comprehensive care',
          yearEstablished: '2020',
          contactPhone: '(555) 123-4567',
          services: ['Individual Therapy', 'Group Therapy', 'Medication Management'],
          appointmentTypes: ['In-Person', 'Telehealth', 'Emergency']
        });
      }
    }
  };
  
  const loadPatients = async () => {
    try {
      const response = await adminService.getTenantPatients(tenantId);
      
      if (response.success) {
        setPatients(response.data);
      }
    } catch (err) {
      console.error('Error loading patients:', err);
      
      // Mock data for development
      if (process.env.NODE_ENV === 'development') {
        setPatients([
          { _id: '1', name: 'John Doe', email: 'john@example.com', status: 'Active', lastVisit: '2025-04-15' },
          { _id: '2', name: 'Jane Smith', email: 'jane@example.com', status: 'Active', lastVisit: '2025-04-10' }
        ]);
      }
    }
  };
  
  const loadProfessionals = async () => {
    try {
      const response = await adminService.getTenantProfessionals(tenantId);
      
      if (response.success) {
        setProfessionals(response.data);
      }
    } catch (err) {
      console.error('Error loading professionals:', err);
      
      // Mock data for development
      if (process.env.NODE_ENV === 'development') {
        setProfessionals([
          { _id: '1', name: 'Dr. Sarah Johnson', email: 'sarah@mindbridge.com', specialty: 'Clinical Psychologist', status: 'Active' },
          { _id: '2', name: 'Dr. Michael Chen', email: 'michael@mindbridge.com', specialty: 'Psychiatrist', status: 'Active' }
        ]);
      }
    }
  };
  
  // Handle opening modal in view mode
  const handleViewPublicProfile = () => {
    if (clinicSetup) {
      populateFormData();
      setIsEditMode(false);
      setShowClinicModal(true);
    }
  };
  
  // Handle opening modal in edit mode
  const handleEditClinicSetup = () => {
    if (clinicSetup) {
      populateFormData();
      setIsEditMode(true);
      setShowClinicModal(true);
    }
  };
  
  // Switch from view to edit mode
  const switchToEditMode = () => {
    setIsEditMode(true);
  };
  
  // Populate form data from clinic setup
  const populateFormData = () => {
    setBasicFormData({
      primaryEmail: clinicSetup?.primaryEmail || '',
      name: clinicSetup?.clinicName || '',
      location: clinicSetup?.clinicLocation || ''
    });
    
    setPublicProfileData({
      address: clinicSetup?.address || '',
      description: clinicSetup?.description || '',
      yearEstablished: clinicSetup?.yearEstablished || '',
      contactPhone: clinicSetup?.contactPhone || '',
      services: clinicSetup?.services?.length ? clinicSetup.services : [''],
      appointmentTypes: clinicSetup?.appointmentTypes?.length ? clinicSetup.appointmentTypes : ['']
    });
  };
  
  // Handle form field changes
  const handleBasicInputChange = (e) => {
    const { id, value } = e.target;
    setBasicFormData({ ...basicFormData, [id]: value });
  };
  
  const handlePublicProfileInputChange = (e) => {
    const { id, value } = e.target;
    setPublicProfileData({ ...publicProfileData, [id]: value });
  };
  
  // Handle service list changes
  const handleServiceChange = (index, value) => {
    const newServices = [...publicProfileData.services];
    newServices[index] = value;
    setPublicProfileData({ ...publicProfileData, services: newServices });
  };
  
  const addService = () => {
    setPublicProfileData({ ...publicProfileData, services: [...publicProfileData.services, ''] });
  };
  
  const removeService = (index) => {
    const newServices = [...publicProfileData.services];
    newServices.splice(index, 1);
    setPublicProfileData({ ...publicProfileData, services: newServices });
  };
  
  // Handle appointment type changes
  const handleAppointmentTypeChange = (index, value) => {
    const newTypes = [...publicProfileData.appointmentTypes];
    newTypes[index] = value;
    setPublicProfileData({ ...publicProfileData, appointmentTypes: newTypes });
  };
  
  const addAppointmentType = () => {
    setPublicProfileData({ ...publicProfileData, appointmentTypes: [...publicProfileData.appointmentTypes, ''] });
  };
  
  const removeAppointmentType = (index) => {
    const newTypes = [...publicProfileData.appointmentTypes];
    newTypes.splice(index, 1);
    setPublicProfileData({ ...publicProfileData, appointmentTypes: newTypes });
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!basicFormData.primaryEmail?.trim()) {
      errors.primaryEmail = 'Primary email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(basicFormData.primaryEmail)) {
      errors.primaryEmail = 'Please enter a valid email address';
    }
    
    if (!basicFormData.name?.trim()) {
      errors.name = 'Clinic name is required';
    }
    
    if (!basicFormData.location?.trim()) {
      errors.location = 'Clinic location is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      const combinedData = {
        ...basicFormData,
        ...publicProfileData,
        services: publicProfileData.services.filter(service => service.trim() !== ''),
        appointmentTypes: publicProfileData.appointmentTypes.filter(type => type.trim() !== '')
      };
      
      const response = await adminService.updateTenant(tenantId, combinedData);
      
      if (response.success) {
        toast.success('Clinic information updated successfully!');
        setShowClinicModal(false);
        setIsEditMode(false);
        loadClinicSetup();
        loadTenantDetails();
      } else {
        throw new Error(response.message || 'Failed to update clinic information');
      }
    } catch (err) {
      console.error('Error updating clinic:', err);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Close modal
  const closeModal = () => {
    setShowClinicModal(false);
    setIsEditMode(false);
    setFormErrors({});
  };
  
  if (loading) {
    return (
      <div className="tdv-container">
        <div className="tdv-loading">
          <div className="tdv-spinner"></div>
          <p>Loading tenant details...</p>
        </div>
      </div>
    );
  }
  
  if (!tenant) {
    return (
      <div className="tdv-container">
        <div className="tdv-error">
          <p>Tenant not found</p>
          <button onClick={handleBackToTenants} className="tdv-back-button">
            Back to Tenants
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="tdv-container">
      {/* Header with back button */}
      <div className="tdv-header">
        <button onClick={handleBackToTenants} className="tdv-back-button">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="tdv-header-content">
          <h1>{tenant.name}</h1>
          <p className="tdv-tenant-id">{tenant.adminEmail || 'mindbridge@clinic_neurolex.nt'}</p>
        </div>
        
        <div className="tdv-status-badge">
          <span className={`tdv-status ${tenant.active ? 'active' : 'inactive'}`}>
            {tenant.active ? 'Active' : 'Inactive'}
          </span>
          <span className="tdv-creation-date">
            <strong>Creation Date:</strong> {new Date(tenant.createdAt).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
      </div>
      
      {/* Count Cards */}
      <div className="tdv-stats-container">
        <div className="tdv-stat-card">
          <h3>Total Patients</h3>
          <div className="tdv-stat-value">{counts.totalPatients}</div>
        </div>
        
        <div className="tdv-stat-card">
          <h3>Total Professionals</h3>
          <div className="tdv-stat-value">{counts.totalProfessionals}</div>
        </div>
        
        <div className="tdv-stat-card">
          <h3>Journal Entries Logged</h3>
          <div className="tdv-stat-value">{counts.journalEntries}</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="tdv-tabs-container">
        <div className="tdv-tabs">
          <button 
            className={`tdv-tab ${activeTab === 'clinic-setup' ? 'active' : ''}`}
            onClick={() => setActiveTab('clinic-setup')}
          >
            Clinic Account Setup
          </button>
          <button 
            className={`tdv-tab ${activeTab === 'patients' ? 'active' : ''}`}
            onClick={() => setActiveTab('patients')}
          >
            Patients
          </button>
          <button 
            className={`tdv-tab ${activeTab === 'doctors' ? 'active' : ''}`}
            onClick={() => setActiveTab('doctors')}
          >
            Doctors
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="tdv-tab-content">
          {/* Clinic Account Setup Tab */}
          {activeTab === 'clinic-setup' && clinicSetup && (
            <div className="tdv-clinic-setup">
              <div className="tdv-setup-fields">
                <div className="tdv-field-group">
                  <label>Primary Contact Email</label>
                  <input type="text" value={clinicSetup.primaryEmail} readOnly />
                </div>
                
                <div className="tdv-field-row">
                  <div className="tdv-field-group">
                    <label>Clinic Name</label>
                    <input type="text" value={clinicSetup.clinicName} readOnly />
                  </div>
                  
                  <div className="tdv-field-group">
                    <label>Clinic Location</label>
                    <input type="text" value={clinicSetup.clinicLocation} readOnly />
                  </div>
                </div>
              </div>
              
              <div className="tdv-action-buttons">
                <button className="tdv-btn-outline" onClick={handleViewPublicProfile}>
                  View Public Profile
                </button>
                <button className="tdv-btn-primary" onClick={handleEditClinicSetup}>
                  Edit Clinic Account Setup Information
                </button>
              </div>
            </div>
          )}
          
          {/* Patients Tab */}
          {activeTab === 'patients' && (
            <div className="tdv-patients-list">
              {patients.length === 0 ? (
                <div className="tdv-empty-state">
                  <p>No patients registered yet</p>
                </div>
              ) : (
                <table className="tdv-table">
                  <thead>
                    <tr>
                      <th>Patient Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Last Visit</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patients.map(patient => (
                      <tr key={patient._id}>
                        <td>{patient.name}</td>
                        <td>{patient.email}</td>
                        <td>
                          <span className={`tdv-badge ${patient.status.toLowerCase()}`}>
                            {patient.status}
                          </span>
                        </td>
                        <td>{new Date(patient.lastVisit).toLocaleDateString()}</td>
                        <td>
                          <button className="tdv-btn-action">View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          
          {/* Doctors Tab */}
          {activeTab === 'doctors' && (
            <div className="tdv-professionals-list">
              {professionals.length === 0 ? (
                <div className="tdv-empty-state">
                  <p>No professionals registered yet</p>
                </div>
              ) : (
                <table className="tdv-table">
                  <thead>
                    <tr>
                      <th>Professional Name</th>
                      <th>Email</th>
                      <th>Specialty</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {professionals.map(prof => (
                      <tr key={prof._id}>
                        <td>{prof.name}</td>
                        <td>{prof.email}</td>
                        <td>{prof.specialty}</td>
                        <td>
                          <span className={`tdv-badge ${prof.status.toLowerCase()}`}>
                            {prof.status}
                          </span>
                        </td>
                        <td>
                          <button className="tdv-btn-action">View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
      
{/* Clinic Information Modal (View/Edit) */}
      {showClinicModal && clinicSetup && (
        <div className="tm-edit-modal-overlay" onClick={closeModal}>
          <div className="tm-edit-modal-content tm-edit-large-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-edit-modal-header">
              <h2>{isEditMode ? `Edit Tenant: ${tenant.name}` : `Clinic Information: ${tenant.name}`}</h2>
              <button className="tm-edit-modal-close" onClick={closeModal}>×</button>
            </div>
            
            {!isEditMode ? (
              // VIEW MODE
              <div className="tm-edit-tenant-form">
                <div className="tm-edit-form-section">
                  <h3>Basic Information</h3>
                  
                  <div className="tm-edit-form-group">
                    <label>Primary Contact Email</label>
                    <input type="text" value={basicFormData.primaryEmail} readOnly />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Clinic Name</label>
                    <input type="text" value={basicFormData.name} readOnly />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Clinic Location</label>
                    <input type="text" value={basicFormData.location} readOnly />
                  </div>
                </div>
                
                <div className="tm-edit-form-section">
                  <h3>Public Profile Information</h3>
                  
                  <div className="tm-edit-form-group">
                    <label>Clinic Description</label>
                    <textarea value={publicProfileData.description || 'Not provided'} readOnly rows="3" />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Clinic Address</label>
                    <input type="text" value={publicProfileData.address || 'Not provided'} readOnly />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Year Established</label>
                    <input type="text" value={publicProfileData.yearEstablished || 'Not provided'} readOnly />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Contact Number</label>
                    <input type="text" value={publicProfileData.contactPhone || 'Not provided'} readOnly />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Services Offered</label>
                    <textarea 
                      value={publicProfileData.services.filter(s => s).length > 0 
                        ? publicProfileData.services.filter(s => s).join(', ')
                        : 'Not provided'} 
                      readOnly 
                      rows="3"
                    />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Appointment Types</label>
                    <textarea 
                      value={publicProfileData.appointmentTypes.filter(t => t).length > 0 
                        ? publicProfileData.appointmentTypes.filter(t => t).join(', ')
                        : 'Not provided'} 
                      readOnly 
                      rows="2"
                    />
                  </div>
                </div>
                
                <div className="tm-form-actions">
                  <button className="tm-btn-secondary" onClick={closeModal}>Close</button>
                  <button className="tm-btn-primary" onClick={switchToEditMode}>Edit Information</button>
                </div>
              </div>
            ) : (
              // EDIT MODE
              <form onSubmit={handleFormSubmit} className="tm-edit-tenant-form">
                <div className="tm-edit-form-section">
                  <h3>Basic Information</h3>
                  
                  <div className="tm-edit-form-group">
                    <label htmlFor="primaryEmail">Primary Email *</label>
                    <input
                      type="email"
                      id="primaryEmail"
                      value={basicFormData.primaryEmail}
                      onChange={handleBasicInputChange}
                      className={formErrors.primaryEmail ? 'tm-error' : ''}
                      placeholder="admin@clinic.com"
                    />
                    {formErrors.primaryEmail && (
                      <span className="tm-error-text">{formErrors.primaryEmail}</span>
                    )}
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label htmlFor="name">Clinic Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={basicFormData.name}
                      onChange={handleBasicInputChange}
                      className={formErrors.name ? 'tm-error' : ''}
                      placeholder="Enter clinic name"
                    />
                    {formErrors.name && (
                      <span className="tm-error-text">{formErrors.name}</span>
                    )}
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label htmlFor="location">Clinic Location *</label>
                    <input
                      type="text"
                      id="location"
                      value={basicFormData.location}
                      onChange={handleBasicInputChange}
                      className={formErrors.location ? 'tm-error' : ''}
                      placeholder="City, State"
                    />
                    {formErrors.location && (
                      <span className="tm-error-text">{formErrors.location}</span>
                    )}
                  </div>
                </div>
                
                <div className="tm-edit-form-section">
                  <h3>Public Profile Information</h3>
                  
                  <div className="tm-edit-form-group">
                    <label htmlFor="description">Clinic Description</label>
                    <textarea
                      id="description"
                      value={publicProfileData.description}
                      onChange={handlePublicProfileInputChange}
                      placeholder="Describe your clinic's specialties and focus"
                      rows="3"
                    />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label htmlFor="address">Clinic Address</label>
                    <input
                      type="text"
                      id="address"
                      value={publicProfileData.address}
                      onChange={handlePublicProfileInputChange}
                      placeholder="Full street address"
                    />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label htmlFor="yearEstablished">Year Established</label>
                    <input
                      type="number"
                      id="yearEstablished"
                      value={publicProfileData.yearEstablished}
                      onChange={handlePublicProfileInputChange}
                      placeholder="YYYY"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label htmlFor="contactPhone">Contact Number</label>
                    <input
                      type="tel"
                      id="contactPhone"
                      value={publicProfileData.contactPhone}
                      onChange={handlePublicProfileInputChange}
                      placeholder="Clinic phone number"
                    />
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Services Offered</label>
                    {publicProfileData.services.map((service, index) => (
                      <div key={`service-${index}`} className="tm-dynamic-input-row">
                        <input
                          type="text"
                          value={service}
                          onChange={(e) => handleServiceChange(index, e.target.value)}
                          placeholder="Enter a service"
                          className="tm-dynamic-input"
                        />
                        {index === publicProfileData.services.length - 1 && (
                          <button 
                            type="button" 
                            onClick={addService}
                            className="tm-btn-icon add"
                            title="Add Service"
                          >
                            +
                          </button>
                        )}
                        {index > 0 && (
                          <button 
                            type="button" 
                            onClick={() => removeService(index)}
                            className="tm-btn-icon remove"
                            title="Remove Service"
                          >
                            −
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="tm-edit-form-group">
                    <label>Appointment Types</label>
                    {publicProfileData.appointmentTypes.map((type, index) => (
                      <div key={`appointment-${index}`} className="tm-dynamic-input-row">
                        <input
                          type="text"
                          value={type}
                          onChange={(e) => handleAppointmentTypeChange(index, e.target.value)}
                          placeholder="Enter an appointment type"
                          className="tm-dynamic-input"
                        />
                        {index === publicProfileData.appointmentTypes.length - 1 && (
                          <button 
                            type="button" 
                            onClick={addAppointmentType}
                            className="tm-btn-icon add"
                            title="Add Appointment Type"
                          >
                            +
                          </button>
                        )}
                        {index > 0 && (
                          <button 
                            type="button" 
                            onClick={() => removeAppointmentType(index)}
                            className="tm-btn-icon remove"
                            title="Remove Appointment Type"
                          >
                            −
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="tm-form-actions">
                  <button type="button" className="tm-btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="tm-btn-primary" disabled={submitting}>
                    {submitting ? 'Updating...' : 'Update Information'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDetailView;